import ChartData from '../../model/type/chart-data.type';
import { Constants } from '../../constants';
import { isNumber } from 'lodash';
import { JournalEntry } from '../../model/dto/journal-entry/journal-entry';

export interface HormoneDetailsRow {
  label: string;
  getValue: (data: ChartData) => unknown;
  formatValue?: (value: unknown) => string;
  sourceField?: string;
  showWearableSummaryAfter?: boolean;
}

export class HormoneDetailsUtils {
  static formatDefaultValue(value: unknown): string {
    return `${value}`;
  }

  static getAutofillProvider(
    journalEntry: JournalEntry | undefined,
    row: HormoneDetailsRow
  ): string | undefined {
    return row.sourceField
      ? journalEntry?.autofill_sources?.[row.sourceField]
      : undefined;
  }

  static readonly detailRows: HormoneDetailsRow[] = [
    {
      label: 'common.dailyData.temperature',
      getValue: (data) => data?.dailyData?.journal_entry?.temperature,
      sourceField: 'temperature',
      formatValue: (value) =>
        isNumber(value)
          ? `${value} ${Constants.TEMPERATURE_UNIT_OF_MEASURE}`
          : '-',
    },
    {
      label: 'common.dailyData.stool',
      getValue: (data) => data?.dailyData?.journal_entry?.stool,
      sourceField: 'stool',
    },
    {
      label: 'common.dailyData.skin',
      getValue: (data) => data?.dailyData?.journal_entry?.skin,
      sourceField: 'skin',
    },
    {
      label: 'common.dailyData.hair',
      getValue: (data) => data?.dailyData?.journal_entry?.hair,
      sourceField: 'hair',
    },
    {
      label: 'common.dailyData.digestion',
      getValue: (data) => data?.dailyData?.journal_entry?.digestion,
      sourceField: 'digestion',
    },
    {
      label: 'common.dailyData.cravings',
      getValue: (data) => data?.dailyData?.journal_entry?.cravings,
      sourceField: 'cravings',
    },
    {
      label: 'common.dailyData.pain',
      getValue: (data) => data?.dailyData?.journal_entry?.pain,
      sourceField: 'pain',
    },
    {
      label: 'common.dailyData.cervicalPosition',
      getValue: (data) => data?.dailyData?.journal_entry?.cervical_position,
      sourceField: 'cervical_position',
    },
    {
      label: 'common.dailyData.cervicalFluid',
      getValue: (data) => data?.dailyData?.journal_entry?.cervical_fluid,
      sourceField: 'cervical_fluid',
    },
    {
      label: 'common.dailyData.period',
      getValue: (data) => data?.dailyData?.journal_entry?.bleeding,
      sourceField: 'bleeding',
    },
    {
      label: 'common.dailyData.breakThroughBleeding',
      getValue: (data) => data?.dailyData?.journal_entry?.breakthrough_bleeding,
      sourceField: 'breakthrough_bleeding',
    },
    {
      label: 'common.dailyData.hotFlashes',
      getValue: (data) => data?.dailyData?.journal_entry?.hot_flashes,
      sourceField: 'hot_flashes',
    },
    {
      label: 'common.dailyData.breastTenderness',
      getValue: (data) => data?.dailyData?.journal_entry?.breast_tenderness,
      sourceField: 'breast_tenderness',
    },
    {
      label: 'common.dailyData.cramps',
      getValue: (data) => data?.dailyData?.journal_entry?.cramps,
      sourceField: 'cramps',
    },
    {
      label: 'common.dailyData.nausea',
      getValue: (data) => data?.dailyData?.journal_entry?.nausea,
      sourceField: 'nausea',
    },
    {
      label: 'common.dailyData.jointPain',
      getValue: (data) => data?.dailyData?.journal_entry?.joint_pain,
      sourceField: 'joint_pain',
    },
    {
      label: 'common.dailyData.backPain',
      getValue: (data) => data?.dailyData?.journal_entry?.back_pain,
      sourceField: 'back_pain',
    },
    {
      label: 'common.dailyData.headache',
      getValue: (data) => data?.dailyData?.journal_entry?.headache,
      sourceField: 'headache',
    },
    {
      label: 'common.dailyData.migraine',
      getValue: (data) => data?.dailyData?.journal_entry?.migraine,
      sourceField: 'migraine',
    },
    {
      label: 'common.dailyData.bodyHairGrowth',
      getValue: (data) => data?.dailyData?.journal_entry?.body_hair_growth,
      sourceField: 'body_hair_growth',
    },
    {
      label: 'common.dailyData.skinChanges',
      getValue: (data) => data?.dailyData?.journal_entry?.skin_changes,
      sourceField: 'skin_changes',
    },
    {
      label: 'common.dailyData.acne',
      getValue: (data) => data?.dailyData?.journal_entry?.acne,
      sourceField: 'acne',
    },
    {
      label: 'common.dailyData.painfulUrination',
      getValue: (data) => data?.dailyData?.journal_entry?.painful_urination,
      sourceField: 'painful_urination',
    },
    {
      label: 'common.dailyData.urinationFrequency',
      getValue: (data) => data?.dailyData?.journal_entry?.urination_frequency,
      sourceField: 'urination_frequency',
    },
    {
      label: 'common.dailyData.incontinence',
      getValue: (data) => data?.dailyData?.journal_entry?.incontinence,
      sourceField: 'incontinence',
    },
    {
      label: 'common.dailyData.sexualActivity',
      getValue: (data) => data?.dailyData?.journal_entry?.sexual_activity,
      sourceField: 'sexual_activity',
    },
    {
      label: 'common.dailyData.sexDrive',
      getValue: (data) => data?.dailyData?.journal_entry?.sex_drive,
      sourceField: 'sex_drive',
    },
    {
      label: 'common.dailyData.exerciseIntesity',
      getValue: (data) => data?.dailyData?.journal_entry?.exercise_intensity,
      sourceField: 'exercise_intensity',
    },
    {
      label: 'common.dailyData.exercisePerceivedEffort',
      getValue: (data) =>
        data?.dailyData?.journal_entry?.exercise_perceived_effort,
      sourceField: 'exercise_perceived_effort',
    },
    {
      label: 'common.dailyData.hoursOfSleep',
      getValue: (data) => data?.dailyData?.journal_entry?.hours_of_sleep,
      sourceField: 'hours_of_sleep',
      showWearableSummaryAfter: true,
    },
    {
      label: 'common.dailyData.sleepQuality',
      getValue: (data) => data?.dailyData?.journal_entry?.sleep_quality,
      sourceField: 'sleep_quality',
    },
    {
      label: 'common.dailyData.dreamType',
      getValue: (data) => data?.dailyData?.journal_entry?.dream_type,
      sourceField: 'dream_type',
    },
    {
      label: 'common.dailyData.overallMood',
      getValue: (data) => data?.dailyData?.journal_entry?.overall_mood,
      sourceField: 'overall_mood',
    },
    {
      label: 'common.dailyData.anxiety',
      getValue: (data) => data?.dailyData?.journal_entry?.anxiety,
      sourceField: 'anxiety',
    },
    {
      label: 'common.dailyData.motivationLevel',
      getValue: (data) => data?.dailyData?.journal_entry?.motivation_level,
      sourceField: 'motivation_level',
    },
    {
      label: 'common.dailyData.productivityLevel',
      getValue: (data) => data?.dailyData?.journal_entry?.productivity_level,
      sourceField: 'productivity_level',
    },
    {
      label: 'common.dailyData.focus',
      getValue: (data) => data?.dailyData?.journal_entry?.focus,
      sourceField: 'focus',
    },
    {
      label: 'common.dailyData.brainFog',
      getValue: (data) => data?.dailyData?.journal_entry?.brain_fog,
      sourceField: 'brain_fog',
    },
    {
      label: 'common.dailyData.memoryProblems',
      getValue: (data) => data?.dailyData?.journal_entry?.memory_problems,
      sourceField: 'memory_problems',
    },
    {
      label: 'common.dailyData.energyLevel',
      getValue: (data) => data?.dailyData?.journal_entry?.energy_level,
      sourceField: 'energy_level',
    },
    {
      label: 'common.dailyData.emotionalStability',
      getValue: (data) => data?.dailyData?.journal_entry?.emotional_stability,
      sourceField: 'emotional_stability',
    },
    {
      label: 'common.dailyData.stress',
      getValue: (data) => data?.dailyData?.journal_entry?.stress,
      sourceField: 'stress',
    },
    {
      label: 'common.dailyData.mental',
      getValue: (data) => data?.dailyData?.journal_entry?.mental,
      sourceField: 'mental',
    },
    {
      label: 'common.dailyData.birthControl',
      getValue: (data) => data?.dailyData?.journal_entry?.birth_control_pill,
      sourceField: 'birth_control_pill',
    },
    {
      label: 'common.dailyData.medication',
      getValue: (data) => data?.dailyData?.journal_entry?.medication,
      sourceField: 'medication',
    },
    {
      label: 'common.dailyData.injection',
      getValue: (data) => data?.dailyData?.journal_entry?.injection,
      sourceField: 'injection',
    },
    {
      label: 'common.dailyData.ailment',
      getValue: (data) => data?.dailyData?.journal_entry?.ailment,
      sourceField: 'ailment',
    },
    {
      label: 'common.dailyData.drinking',
      getValue: (data) => data?.dailyData?.journal_entry?.drinking,
      sourceField: 'drinking',
    },
    {
      label: 'common.dailyData.cannabis',
      getValue: (data) => data?.dailyData?.journal_entry?.cannabis,
      sourceField: 'cannabis',
    },
    {
      label: 'common.dailyData.hangover',
      getValue: (data) => data?.dailyData?.journal_entry?.hangover,
      sourceField: 'hangover',
    },
    {
      label: 'common.dailyData.bloating',
      getValue: (data) => data?.dailyData?.journal_entry?.bloating,
      sourceField: 'bloating',
    },
    {
      label: 'common.dailyData.headHair',
      getValue: (data) => data?.dailyData?.journal_entry?.head_hair,
      sourceField: 'head_hair',
    },
    {
      label: 'common.dailyData.bloating',
      getValue: (data) => data?.dailyData?.journal_entry?.bloating,
      sourceField: 'bloating',
    },
    {
      label: 'common.dailyData.bloating',
      getValue: (data) => data?.dailyData?.journal_entry?.bloating,
      sourceField: 'bloating',
    },
    {
      label: 'common.dailyData.spikeInfo.restingHr',
      getValue: (data) => data?.dailyData?.spike_info?.resting_hr,
      formatValue: (value) => `${value} ${Constants.HEART_RATE_UNIT}`,
    },
    {
      label: 'common.dailyData.spikeInfo.avgHr',
      getValue: (data) => data?.dailyData?.spike_info?.avg_hr,
      formatValue: (value) => `${value} ${Constants.HEART_RATE_UNIT}`,
    },
    {
      label: 'common.dailyData.spikeInfo.floors',
      getValue: (data) => data?.dailyData?.spike_info?.floors,
    },
    {
      label: 'common.dailyData.spikeInfo.steps',
      getValue: (data) => data?.dailyData?.spike_info?.steps,
    },
    {
      label: 'common.dailyData.spikeInfo.caloriesTotal',
      getValue: (data) => data?.dailyData?.spike_info?.calories_total,
      formatValue: (value) => `${value} ${Constants.CALORIES_UNIT}`,
    },
  ];
}
